import Component from '@glimmer/component';

import { registerDestructor } from '@ember/destroyable';
import { hash } from '@ember/helper';
import { action } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { DEBUG } from '@glimmer/env';
import { cached, tracked } from '@glimmer/tracking';

import classMod from 'rise-ui/helpers/class-mod.js';
import classModConfig from 'rise-ui/helpers/class-mod-config.js';
import concatClasses from 'rise-ui/helpers/concat-classes.js';
import emberArray from 'rise-ui/helpers/ember-array.js';
import not from 'rise-ui/helpers/not.js';
import passProperties from 'rise-ui/helpers/pass-properties.js';
import update from 'rise-ui/modifiers/update.js';
import { extentOne } from 'rise-ui/utils/array.js';
import { animate } from 'rise-ui/utils/animate.js';
import { clearCanvas, createCanvas, drawLines, getContext, hintCanvas } from 'rise-ui/utils/canvas.js';
import { DprCanvas } from 'rise-ui/utils/canvas/dpr-canvas.js';
import { drawGraphCrosshair } from 'rise-ui/utils/canvas/graph-crosshair.js';
import { drawGraphText } from 'rise-ui/utils/canvas/graph-text.js';
import { drawGraphAxis } from 'rise-ui/utils/canvas/graph-axis.js';
import { drawGraphLegend } from 'rise-ui/utils/canvas/graph-legend.js';
import { drawGraphNodes } from 'rise-ui/utils/canvas/graph-nodes.js';
import { getShapeByIndex } from 'rise-ui/utils/canvas/graph-shapes.js';
import { drawRectangles } from 'rise-ui/utils/canvas/shapes.js';
import { CollectionGroup, CollectionTransformer } from 'rise-ui/utils/collection.js';
import { alpha } from 'rise-ui/utils/color.js';
import { debugGroupStart, debugGroupEnd as _debugGroupEnd, debugTimeStart, debugTimePause as _debugTimePause, debugTimeEnd as _debugTimeEnd } from 'rise-ui/utils/debug.js';
import { resizeRect, resizeStyleRect, undraggable } from 'rise-ui/utils/dom.js';
import { call } from 'rise-ui/utils/function.js';
import { LinesAndNodes } from 'rise-ui/utils/graph/graph-lines-and-nodes.js';
import { Changes, GraphConfig, scalar } from 'rise-ui/utils/graph-config.js';
import { isSet } from 'rise-ui/utils/nullish.js';
import { clamp } from 'rise-ui/utils/number.js';
import { softSet, softSetProperties } from 'rise-ui/utils/object.js';
import { PixelIndex } from 'rise-ui/utils/pixel-index.js';
import { PixelIndexCursor } from 'rise-ui/utils/pixel-index-cursor.js';
import { ResizeObserver } from 'rise-ui/utils/resize-observer.js';

const features = { lineOrder: true, tooltipEvent: true, alpha: true };
const ctxOptions = { alpha: features.alpha };
const ctxOptionsNoAlpha = { ...ctxOptions, alpha: false };
const ctx = (c, noAlpha = false, ratio = null) =>
  getContext(c, '2d', noAlpha ? ctxOptionsNoAlpha : ctxOptions, ratio);

const debug = {
  messages: false, timing: false, crosshair: false,
  useTooltipBuffer: true, showTooltipBuffer: false, debugTooltipBuffer: false,
};
const debugGroup = label => (debug.messages || debug.timing) && debugGroupStart(label);
const debugGroupEnd = () => (debug.messages || debug.timing) && _debugGroupEnd();
const debugTime = (key, fn) => {
  debug.timing && debugTimeStart(key);
  if (fn) {
    fn();
    debug.timing && debugTimeEnd(key);
  }
};
const debugTimePause = key => debug.timing && _debugTimePause(key);
const debugTimeEnd = key => debug.timing && _debugTimeEnd(key);

const matchClasses = /\S+/g;
function getClasses(classString) {
  return classString && classString.match(matchClasses) || [];
}

let CHANGES = 0;
const CHANGE_RENDER            = ++CHANGES; // Some render is needed
const CHANGE_REDRAW            = ++CHANGES; // Everything needs redraw
const CHANGE_DOMAIN            = ++CHANGES; // Data changed
const CHANGE_WINDOW            = ++CHANGES; // Window changed
const CHANGE_WINDOW_X_LOWER    = ++CHANGES; // Window changed
const CHANGE_WINDOW_X_UPPER    = ++CHANGES; // Window changed
const CHANGE_WINDOW_Y_LOWER    = ++CHANGES; // Window changed
const CHANGE_WINDOW_Y_UPPER    = ++CHANGES; // Window changed
const CHANGE_PIXEL_INDEX       = ++CHANGES; // Pixel index needs refreshing
const CHANGE_SIZE              = ++CHANGES; // Overall width or height changed
const CHANGE_SIZE_WIDTH        = ++CHANGES; // Overall width changed
const CHANGE_SIZE_HEIGHT       = ++CHANGES; // Overall height changed
const CHANGE_X_AXIS            = ++CHANGES; // X-Axis needs redraw
const CHANGE_Y_AXIS            = ++CHANGES; // Y-Axis needs redraw
const CHANGE_LINE_REDRAW       = ++CHANGES; // Lines need redraw
const CHANGE_LINE_NODES        = ++CHANGES; // Nodes need redraw
const CHANGE_LINE_META         = ++CHANGES; // Changes to line-meta data
const CHANGE_LINE_MOUSE        = ++CHANGES; // Mouse activity on lines
const CHANGE_LINE_MOUSE_X      = ++CHANGES; // Mouse x change on lines
const CHANGE_LINE_MOUSE_CLICK  = ++CHANGES; // Mouse lock on lines for tooltip
const CHANGE_LINE_MOUSE_LOCK   = ++CHANGES; // Mouse lock on lines for tooltip

function initChanges(changes) {
  // Drawing skipped unless one of these is set each frame
  changes.changeWith(CHANGE_RENDER,
    CHANGE_REDRAW,
    CHANGE_LINE_REDRAW,
    CHANGE_LINE_MOUSE,
    CHANGE_LINE_NODES,
    CHANGE_LINE_META,
  );

  // Full redraw on domain or size change
  changes.changeWith(CHANGE_REDRAW,
    CHANGE_DOMAIN,
    CHANGE_WINDOW,
    CHANGE_SIZE,
  );

  // Recalculate pixel indexes on domain or width change
  changes.changeWith(CHANGE_PIXEL_INDEX,
    CHANGE_DOMAIN,
    CHANGE_WINDOW,
    CHANGE_SIZE_WIDTH,
  );

  // Recalculate window only if a bound actually changes
  changes.changeWith(CHANGE_WINDOW,
    CHANGE_WINDOW_X_LOWER,
    CHANGE_WINDOW_X_UPPER,
    CHANGE_WINDOW_Y_LOWER,
    CHANGE_WINDOW_Y_UPPER,
  );

  changes.changeWith(CHANGE_SIZE, CHANGE_SIZE_WIDTH, CHANGE_SIZE_HEIGHT);

  // Draw axes once each full redraw
  changes.changeWith(CHANGE_X_AXIS, CHANGE_REDRAW);
  changes.changeWith(CHANGE_Y_AXIS, CHANGE_REDRAW);

  changes.changeWith(CHANGE_LINE_REDRAW, CHANGE_REDRAW);

  changes.changeWith(CHANGE_LINE_NODES, CHANGE_REDRAW);

  changes.changeWith(CHANGE_LINE_MOUSE,
    CHANGE_REDRAW,
    CHANGE_LINE_MOUSE_CLICK,
    CHANGE_LINE_MOUSE_LOCK,
    CHANGE_LINE_MOUSE_X,
  );

  return changes;
}

/**
 * GraphView input attributes
 * @name GraphViewInputs
 * @interface
 * @extends GraphConfigInput
 * @property {boolean} [animateSize] Whether to animate width/height changes
 * TODO: cursorType=horizontal is not yet supported, as it would require
 *  implementation of another pixel-index mapping.
 */
/**
 * Draws/animates a graph
 * @name GraphView
 * @property {GraphViewInputs} args
 */
export default class GraphView extends Component {
  config = new GraphConfig();

  @service documentEvents;
  @service('debug/time-series-output') timeSeriesOutputs;

  wrapper = undefined;
  @tracked _data;

  changes = initChanges(new Changes());

  @tracked mouseX = 0;
  @tracked mouseY = 0;

  pixelIndex = new PixelIndex(900).setDataKey('xScalar');
  hoverCursor = new PixelIndexCursor(this.pixelIndex);
  pinCursor = new PixelIndexCursor(this.pixelIndex);

  tooltips = [];
  secondaryTooltips = [];

  @cached get isOverGraph() {
    const { config, mouseX, mouseY } = this;
    const { graphTop, graphLeft, graphWidth, graphHeight } = config;
    return mouseX >= graphLeft && mouseX <= graphLeft + graphWidth
      && mouseY >= graphTop && mouseY <= graphTop + graphHeight;
  }

  @cached get graphMouseX() {
    const { config, mouseX } = this;
    const { graphLeft, graphWidth, graphMargin } = config;
    return Math.floor(clamp(0, mouseX - graphLeft - graphMargin, Math.floor(graphWidth - graphMargin - graphMargin - 1)));
  }

  @cached get graphMouseY() {
    const { config, mouseY } = this;
    const { graphTop, graphHeight, padding } = config;
    return clamp(0, mouseY - graphTop - padding, graphHeight);
  }

  linesZIndex = 100;
  nodesZIndex = 200;

  /**
   * @name GraphViewTemplateConfigArg
   * @property {string} [axisClass]
   * @property {string} [bottomAxisClass]
   * @property {string} [leftAxisClass]
   * @property {string} [linesClass]
   * @property {string} [lineClass]
   * @property {string} [tooltipsClass]
   * @property {string} [tooltipClass]
   * @property {string} [verticalCursorClass]
   * @property {string} [verticalSecondaryCursorClass]
   * @property {string} [coverClass]
   * @property {string} [hoverClass]
   * @property {string} [activeLineClass]
   * @property {string} [inactiveLineClass]
   * @property {string} [tooltipCanvasClass]
   */
  /**
   * @param wrapper
   * @param {GraphViewTemplateConfigArg} templateConfig
   */
  @action didInsertWrapper(wrapper, [ templateConfig ]) {
    this.templateConfig = templateConfig;
    this.wrapper = undraggable(wrapper);
    wrapper.style.position = 'relative';

    const { width, height } = this.config;

    const addToWrapper = (el, style = {}) => {
      undraggable(el);
      Object.assign(el.style, style);
      this.wrapper.appendChild(el);
      return el;
    };

    // Create and insert main canvas into wrapper at initial dimensions
    const canvas = hintCanvas('main', undraggable(
      createCanvas(width, height, '#fff', { alpha: false })
    ));
    this.mainCanvas = new DprCanvas(canvas);
    wrapper.appendChild(canvas);

    const { colorGraph } = this.timeSeriesOutputs;

    this.bottomAxisCanvas = addToWrapper(hintCanvas('bottom-axis',
      createCanvas(0, 0, colorGraph?.xAxisBgColor || '#fff', { alpha: false })
    ));
    this.bottomAxisCanvas.classList.add(
      ...getClasses(templateConfig.axisClass),
      ...getClasses(templateConfig.bottomAxisClass)
    );
    this.leftAxisCanvas = addToWrapper(hintCanvas('left-axis',
      createCanvas(0, 0, colorGraph?.yAxisBgColor || '#fff')
    ));
    this.leftAxisCanvas.classList.add(
      ...getClasses(templateConfig.axisClass),
      ...getClasses(templateConfig.leftAxisClass)
    );

    // Under all lines in case lines needs background-color
    this.lineBackgroundElement = addToWrapper(document.createElement('div'), {
      position: 'absolute',
      backgroundColor: this.timeSeriesOutputs.colorGraph?.graphBgColor || this.args.backgroundColor,
      top: 0,
      left: 0,
      width: `${width}px`,
      height: `${height}px`,
    });

    this.linesEl = addToWrapper(document.createElement('div'));
    this.linesEl.classList.add(...getClasses(templateConfig.linesClass));

    this.tooltipBufferCanvas = hintCanvas('tooltip-buffer',
      createCanvas(0, 0, features.alpha ? null : '#fff', ctxOptions)
    );
    if (debug.showTooltipBuffer) {
      this.tooltipBufferCanvas.classList.add('tooltipBufferCanvas');
      addToWrapper(this.tooltipBufferCanvas, {
        zIndex: 10000,
        position: 'absolute',
        left: 0,
        top: 0,
      });
    }
    this.tooltipsEl = addToWrapper(document.createElement('div'));
    this.tooltipsEl.classList.add(...getClasses(templateConfig.tooltipsClass));

    // Vertical cursor for hover or pin while not hovering
    this.verticalCursorEl = addToWrapper(document.createElement('div'));
    this.verticalCursorEl.classList.add(...getClasses(templateConfig.verticalCursorClass));
    // Vertical cursor for pin while also hovering
    this.verticalSecondaryCursorEl = addToWrapper(document.createElement('div'));
    this.verticalSecondaryCursorEl.classList.add(...getClasses(templateConfig.verticalSecondaryCursorClass));

    // Invisible cover prevents canvas-specific right-click action
    const coverEl = this.coverElement = addToWrapper(document.createElement('div'), {
      width: `${width}px`,
      height: `${height}px`,
    });
    coverEl.classList.add(...getClasses(templateConfig.coverClass));

    // Watch size of wrapper to affect size of canvas and redraw
    this.startWatchingSize();

    // Watch mouse movement to determine node under cursor
    coverEl.addEventListener('mousemove', e => this.onMouseMove(e));
    // Watch mouse click to determine node under cursor and activate sticky
    coverEl.addEventListener('click', e => this.onMouseClick(e));

    const exit = () => this.onInteractExit();
    // Stop interaction on mouse leave
    coverEl.addEventListener('mouseleave', exit);
    // Also stop interaction if user has no mouse but clicks outside cover
    const removeDocumentClickEvent = this.documentEvents.on('click', {
      when: () => this.isInteracting,
      isNot: coverEl,
      then: exit,
    });

    registerDestructor(this, () => {
      removeDocumentClickEvent();
      this.sizeWatcher.disconnect();
      this.sizeWatcher = undefined;
      this.stopAnimation();
      call(this.args.onDestroy, this, this);
    });

    call(this.args.onInit, this, this);
  }

  sizeWatcher = undefined;

  // Update graph width/height based on wrapper element size
  startWatchingSize() {
    const { input } = this.config;

    this.sizeWatcher = new ResizeObserver(([ entry ]) => {
      const cbs = entry.contentBoxSize;
      const cr = entry.contentRect;
      const [ width, height ] = cbs ? [ cbs[0].inlineSize, cbs[0].blockSize ] : [ cr.width, cr.height ];
      input.width = width;
      input.height = height;
    });
    this.sizeWatcher.observe(this.wrapper);
  }

  @action onInterfaceObjectChange() {
    later(() => {
      const { interface: iface } = this.args
      if (typeof iface !== 'object') {
        return;
      }
      softSetProperties(iface, {
        isPinned: this._isPinned,
        isPinnable: this.isPinnable,
        isOverGraph: this.graphMouseX !== null,
        isMouseMove: false,
        unpin: () => this.unpinGraphMouse(),
      });
    });
  }

  @action onCursorChange() {
    const { verticalCursorEl, verticalSecondaryCursorEl } = this;
    verticalCursorEl.style.display = 'none';
    verticalSecondaryCursorEl.style.display = 'none';
  }

  setInterfaceProperty(key, value) {
    later(() => {
      const { interface: iface } = this.args
      if (typeof iface !== 'object') {
        return;
      }
      softSet(iface, key, value);
    });
  }

  pinGraphMouse() {
    this.pinCursor.setPosition(this.graphMouseX).activate();
    this.changes.change(CHANGE_LINE_MOUSE_LOCK, true);
  }

  unpinGraphMouse() {
    this.pinCursor.setPosition();
    this.changes.change(CHANGE_LINE_MOUSE_LOCK, false);
  }

  setLineMousePosition(x = null, y = null) {
    let isOverGraph = x !== null && y !== null;
    if (!isOverGraph) {
      this.changes.set(CHANGE_LINE_MOUSE_CLICK, false);
      const { wrapper, templateConfig: { hoverClass } } = this;
      isOverGraph = false;
      const hoverClasses = getClasses(hoverClass);
      wrapper.classList.remove(...hoverClasses);
    }

    if (!isOverGraph) {
      this.hoverCursor.setPosition();
      this.changes.set(CHANGE_LINE_MOUSE, true);
    }
    this.changes.set(CHANGE_LINE_MOUSE, true);
    this.setInterfaceProperty('isOverGraph', isOverGraph);
    this.setInterfaceProperty('isPinnable', this.isPinnable = x !== null && this.isPinnable);
  }

  // Called when mouse moves or is clicked
  setMousePosition(e, click) {
    const { config, mainCanvas, wrapper, templateConfig: { hoverClass }, hoverCursor, pinCursor } = this;
    const { graphTop, graphLeft, graphMargin } = config;
    // FIXME don't use getBoundingClientRect
    const rect = mainCanvas.getCanvas().getBoundingClientRect();
    let [ mouseX, mouseY ] = [ Math.floor(e.clientX - rect.left), Math.floor(e.clientY - rect.top) ];
    if (softSetProperties(this, { mouseX, mouseY })) {
      this.changes.set(CHANGE_LINE_MOUSE, true);
    }
    const [ mx, my ] = [ mouseX - graphLeft - graphMargin, mouseY - graphTop - graphMargin ];
    const hoverClasses = getClasses(hoverClass);

    const { graphMouseX } = this;
    hoverCursor.setPosition(graphMouseX).activate();
    if (this.isOverGraph) {
      wrapper.classList.add(...hoverClasses);
    } else {
      wrapper.classList.remove(...hoverClasses);
    }
    this.setLineMousePosition(mx, my);
  }

  onMouseMove(e) {
    // True if mousemove ever fired
    this.setInterfaceProperty('isMouseMove', true);
    // Unset mouse click flag
    this.setInterfaceProperty('isMouseClick', false);
    this.setMousePosition(e);
    this.onInteract();
  }

  onMouseClick(e) {
    this.changes.set(CHANGE_LINE_MOUSE_CLICK, true);
    // Set mouse click flag; usable in conditions against devices that have no mouse move/leave
    this.setInterfaceProperty('isMouseClick', true);
    this.setMousePosition(e, true);
    this.onInteract();
  }

  isInteracting = false;

  onInteract() {
    if (this.isInteracting) {
      return;
    }
    this.isInteracting = true;
    call(this.args.onInteract, this, this);
  }

  onInteractExit() {
    if (!this.isInteracting) {
      return;
    }
    this.isInteracting = false;
    call(this.args.onInteractExit, this, this);
  }

  clearHover() {
    this.setLineMousePosition();
  }

  animation = undefined;
  startAnimation() {
    if (this.animation) {
      return;
    }
    this.animation = animate(() => this.draw(), 60);
  }
  stopAnimation() {
    if (this.animation) {
      this.animation = this.animation();
    }
  }

  @action onLineMetaChange() {
    this.changes.set(CHANGE_LINE_META, true);
  }

  @action onTooltipSizeChange() {
    let { tooltipCanvasWidth: width, tooltipCanvasHeight: height } = this.args;
    if (!width || !height) {
      this.tooltipCanvasSize = 0;
      return;
    }
    const el = document.createElement('span');
    el.style.position = 'absolute';
    el.style.visible = 'hidden';
    el.style.width = width;
    el.style.height = height;
    document.body.appendChild(el);
    [ width, height ] = [ el.clientWidth, el.clientHeight ];
    this.tooltipCanvasSize = width && height && { width, height };
    el.remove();
  }

  @action onDataKeyColorChange() {
    this.unpinGraphMouse();
    this.changes.set(CHANGE_LINE_REDRAW, true);
  }

  @action onDataChange() {
    this.unpinGraphMouse();
    this.changes.set(CHANGE_DOMAIN, true);
  }

  @action onDomainChange() {
    const { xLowerDomain, xUpperDomain } = this.args;
    this.pixelIndex.setDomain([ scalar(xLowerDomain), scalar(xUpperDomain) ]);
    // Set each bound and if any changed CHANGE_WINDOW will be set
    this.changes.set(CHANGE_DOMAIN, true)
    this.unpinGraphMouse();
  }

  @action onWindowChange() {
    // FIXME onWindowChange called with too many times:
    // When the data comes in, the graph picks it up first and recalculates, but
    // then the scroll control picks up the data and changes the window again
    const { xLowerWindow, xUpperWindow, yLowerWindow, yUpperWindow } = this.args;

    // Set each bound and if any changed CHANGE_WINDOW will be set
    this.changes
      .change(CHANGE_WINDOW_X_LOWER, xLowerWindow)
      .change(CHANGE_WINDOW_X_UPPER, xUpperWindow)
      .change(CHANGE_WINDOW_Y_LOWER, yLowerWindow)
      .change(CHANGE_WINDOW_Y_UPPER, yUpperWindow);

    // Nothing to do if values match previous
    if (!this.changes.consume(CHANGE_WINDOW)) {
      return;
    }

    const xLowerDomain = scalar(this.args.xLowerDomain);
    const xUpperDomain = scalar(this.args.xUpperDomain);
    const diff = xUpperDomain - xLowerDomain;
    const l = (xLowerWindow - xLowerDomain) / (diff || 1);
    const h = 1 - (xUpperDomain - xUpperWindow) / (diff || 1);

    this.pinCursor.setPosition();
    this.clearHover();
    this.unpinGraphMouse();
    this.pixelIndex.setWindow([ l, h ]);
    this.resetTooltips();
  }

  @action onScaleChange() {
    this.changes.set(CHANGE_REDRAW, true);
  }

  @action onSizeChange() {
    let { width, height } = this.config;
    width = width || 0;
    height = height || 0;
    this.unpinGraphMouse();
    this.changes.change(CHANGE_SIZE_WIDTH, width);
    this.changes.change(CHANGE_SIZE_HEIGHT, height);
    if (!width || !height) {
      this.stopAnimation();
      const canvas = this.mainCanvas;
      if (canvas) {
        resizeRect(canvas.getCanvas(), width, height, window.devicePixelRatio);
        resizeStyleRect(this.coverElement.style, width, height);
      }
    } else {
      this.startAnimation();
    }
  }

  /** @type CollectionGroup */
  modelRunMemberVertexGroup;

  resetIfNotPaginated() {
    let { paginated } = this.args;
    if (typeof paginated === 'function') {
      paginated = paginated();
    }
    if (paginated && this.xDomain) {
      return;
    }
    this.dataKeys = [];
    this._data = [];
    // Running domains of [paginated] data
    this.xDomain = extentOne();
    this.yDomain = extentOne();
    // Whether any nulls exist within data
    this.hasNulls = false;
    // Processes and transforms new vertices
    this.vertexProcesser = new CollectionTransformer(this._data, this.mapNewVertexData);
    // Groups vertices into discrete model-run-member lines
    this.modelRunMemberVertexGroup = new CollectionGroup(({ groupId }) => groupId);
  }

  getModelRunMemberId(result) {
    return call(this.args.dataKey, result[this.args.dataKey], result);
  }

  @action isVertexSet({ yValue }) {
    const isVertexSet = isSet(yValue);
    this.hasNulls ||= !isVertexSet;
    return isVertexSet;
  }

  @action mapNewVertexData(result, i) {
    const { xAxisValue, yAxisValue } = this.config;
    const xValue = xAxisValue(result, i);
    const yValue = yAxisValue(result, i);
    this.xDomain = extentOne(xValue, this.xDomain);
    this.yDomain = extentOne(yValue, this.yDomain);
    /**
     * @name GraphViewVertex
     * @property {number|undefined} groupId Model-run-member ID, if applicable
     * @property {ModelRunMemberLine} group Model-run-member/line group
     * @property {*} xValue Raw value of "x" field of result datum
     * @property {*} yValue Raw value of "y" field of result datum
     * @property {*} xScalar Scalarized value of xValue
     * @property {*} yValue Scalarized value of yValue
     * @property {number} x X position of vertex, updated each draw in vertexTransform
     * @property {number} y Y position of vertex, updated each draw in vertexTransform
     * @property {number} transformRenderIndex Tracks last renderIndex to prevent re-transformation
     */
    const vertex = this._data[i] = Object.assign({}, {
      groupId: this.getModelRunMemberId(result),
      xValue, yValue,
      xScalar: scalar(xValue), yScalar: scalar(yValue),
      x: 0, y: 0,
    });
    vertex.group = this.addVertexToModelRunMemberLine(vertex);
    this.pixelIndex.addDataIndex(i);
  }

  /**
   * @typedef ModelRunMemberLine
   * @extends CollectionGroupItemGroup
   * @property {HTMLCanvasElement} lineCanvas
   * @property {CanvasRenderingContext2D} lineContext
   * @property {HTMLCanvasElement} tooltipCanvas
   * @property {CanvasRenderingContext2D} tooltipContext
   * @property {LinesAndNodes} linesAndNodes
   * @property {string} dataKey
   * @property {number} dataKeyIndex
   * @property {function():string} dataKeyColor
   * @property {function(any, any, any, any, boolean=, boolean=)} drawNode
   * @property {boolean} isVisible
   * @property {boolean} hasBecomeVisible
   */
  /**
   * Get/create and maintain life-cycle on model-run-member group for given item
   * @param {*} item
   * @return {ModelRunMemberLine}
   */
  @action addVertexToModelRunMemberLine(item) {
    const group = /** @type ModelRunMemberLine */
      this.modelRunMemberVertexGroup.group(item);
    group.items.length === 1 && this.onNewModeRunMemberGroup(group);
    return group;
  }

  /**
   * Setup associated meta-data for new model-run-member group
   * @param {ModelRunMemberLine} group
   */
  @action onNewModeRunMemberGroup(group) {
    const groups = this.modelRunMemberVertexGroup;
    group.dataKey = group.group;
    const dataKeyIndex = call(this.args.dataKeyIndex, group.index, group.dataKey);
    group.dataKeyIndex = dataKeyIndex;
    const { dataKeyColors, dataKeyColorsCyclic } = this.args;
    group.dataKeyColor = () => {
      if (groups.itemGroups.length <= 1 || !dataKeyColors) {
        return this.args.lineColor;
      }
      const max = (groups.itemGroups.length - (dataKeyColorsCyclic ? 0 : 1)) || 1;
      return dataKeyColors(dataKeyIndex / max);
    }
    group.lineCanvas = this.createLineCanvas(group);
    group.linesAndNodes = new LinesAndNodes(group.items, this.isVertexSet);
    group.tooltipCanvas = this.createTooltipCanvas(group);
    group.drawNode = getShapeByIndex(dataKeyIndex);
    if (features.lineOrder) {
      group.lineCanvas.style.zIndex = '' + this.linesZIndex + group.index;
      group.tooltipCanvas.style.zIndex = '' + this.nodesZIndex + group.index;
    }
  }

  @action processNewModelRunMemberItems(group) {
    group.linesAndNodes.processNewVertices();
  }

  createLineCanvas(group) {
    const lineClasses = getClasses(this.templateConfig.lineClass);
    const canvas = hintCanvas(`line-${group?.index || 0}`, undraggable(createCanvas(
      0, 0, features.alpha ? null : '#fff', ctxOptions
    )));
    canvas.classList.add(...lineClasses);
    this.linesEl.appendChild(canvas);
    canvas.style.zIndex = this.linesZIndex;
    return canvas;
  }

  getLineContext(canvas) {
    return ctx(canvas);
  }

  @action clearLineCanvas(canvas, index) {
    const { graphWidth, graphHeight } = this.config;
    clearCanvas(canvas, features.alpha ? null : '#fff', graphWidth, graphHeight, ctxOptions);
  }

  createTooltipCanvas(group) {
    const tooltipClasses = getClasses(this.templateConfig.tooltipClass);
    const canvas = hintCanvas(`tooltip-${group?.index || 0}`, undraggable(createCanvas(
      0, 0, features.alpha ? null : '#fff', ctxOptions
    )));
    canvas.classList.add(...tooltipClasses);
    this.tooltipsEl.appendChild(canvas);
    canvas.style.zIndex = this.nodesZIndex;
    return canvas;
  }

  getTooltipContext(canvas) {
    return ctx(canvas);
  }

  @action clearTooltipCanvas(canvas) {
    const { graphWidth, graphHeight } = this.config;
    clearCanvas(canvas, features.alpha ? null : '#fff', graphWidth, graphHeight, ctxOptions);
  }

  createTooltipLegendCanvas() {
    const { tooltipCanvasSize: { width, height } } = this;
    const tooltipLegendClasses = getClasses(this.templateConfig.tooltipCanvasClass);
    const canvas = hintCanvas('tooltip-legend', undraggable(createCanvas(
      width, height, features.alpha ? null : '#fff', ctxOptions
    )));
    canvas.classList.add(...tooltipLegendClasses);
    return canvas;
  }

  getTooltipLegendContext(canvas) {
    return ctx(canvas);
  }

  bindDomain() {
    if (!this.changes.consume(CHANGE_DOMAIN)) {
      return;
    }
    debugTime('domain');
    this.resetIfNotPaginated();
    this.pixelIndex.setData(this._data);
    this.vertexProcesser.setSourceItems(this.args.data?.arrangedContent || this.args.data || []);
    this.vertexProcesser.processNewItems();
    this.modelRunMemberVertexGroup
      .forEach(this.processNewModelRunMemberItems);
    // TODO remove after invalidatePixelByIndex is implemented (called in addDataIndex)
    this.pixelIndex.invalidatePixels();
    this._data = this._data; // Update tracking
    this.config.input.dataKeys = this.dataKeys;
    debugTimeEnd('domain');
  }

  @action setDomain() {
    this.bindDomain();

    debugTime('window');

    const { xLowerWindow, xUpperWindow, yLowerWindow, yUpperWindow, onDomain } = this.args;
    const { dataKeys, xDomain, yDomain } = this;
    const { input } = this.config;

    const xBoundDomain = [ xLowerWindow ?? xDomain[0], xUpperWindow ?? xDomain[1] ];
    const yBoundDomain = [ yLowerWindow ?? yDomain[0], yUpperWindow ?? yDomain[1] ];

    let isSet = false;

    // Set accumulated values for dependant CPs
    if (!input.xDomain
      || input.xDomain[0] !== xBoundDomain[0] || input.xDomain[1] !== xBoundDomain[1]
    ) {
      input.xDomain = xBoundDomain;
      isSet = true;
    }
    if (!input.yDomain
      || input.yDomain[0] !== yBoundDomain[0] || input.yDomain[1] !== yBoundDomain[1]
    ) {
      input.yDomain = yBoundDomain;
      isSet = true;
    }

    if (isSet) {
      call(onDomain, null, {
        x: xBoundDomain,
        y: yBoundDomain,
      }, this._data.length, dataKeys, {
        x: xDomain,
        y: yDomain,
      }, this.hasNulls);
    }

    debugTimeEnd('window');

  }

  @cached get activeLineClasses() {
    return getClasses(this.templateConfig.activeLineClass);
  }

  @cached get inactiveLineClasses() {
    return getClasses(this.templateConfig.inactiveLineClass);
  }

  // Adds appropriate classes depending on which of multiple lines are active/inactive
  @action activateLine(lineEl, active = null) {
    const { activeLineClasses, inactiveLineClasses } = this;
    if (active !== null) {
      if (active) {
        lineEl.classList.remove(...inactiveLineClasses);
        lineEl.classList.add(...activeLineClasses);
      } else {
        lineEl.classList.remove(...activeLineClasses);
        lineEl.classList.add(...inactiveLineClasses);
      }
    } else {
      lineEl.classList.remove(...inactiveLineClasses);
      lineEl.classList.remove(...activeLineClasses);
    }
  }

  isRenderingAll = false;
  hasMouseMovedThisRender = false;
  hasLineMetaChangedThisRender = false;

  draw() {

    const { args, changes, config, mainCanvas, graphMouseX, isOverGraph } = this;
    const { cursorType } = args;
    const { width, height } = config;

    changes.change(CHANGE_LINE_MOUSE_X, graphMouseX);

    const needsRender = this.changes.consume(CHANGE_RENDER);
    if (!width || !height || !needsRender) {
      return;
    }

    this.renderIndex = (this.renderIndex ?? -1) + 1;

    const { graphMargin, graphWidth, graphHeight } = config;

    debugGroup(`Graph draw`);
    debugTime('draw');

    this.setDomain();

    const { _data, verticalCursorEl, verticalSecondaryCursorEl, coverElement } = this;
    const { tooltipDistance } = config;

    const data = _data || [];

    const gridHasSize = graphWidth && graphHeight;
    const gridMouse = this.hasMouseMovedThisRender
      = gridHasSize && changes.consume(CHANGE_LINE_MOUSE);
    this.hasLineMetaChangedThisRender = gridHasSize && changes.consume(CHANGE_LINE_META);
    const isMouseClicked = changes.consume(CHANGE_LINE_MOUSE_CLICK);
    this.isRenderingAll = gridHasSize && changes.consume(CHANGE_LINE_REDRAW);

    this.pixelIndex
      .setWidth(Math.ceil(Math.max(0, graphWidth - graphMargin - graphMargin)));
    this.hoverCursor.setRadius(tooltipDistance).updateCursor();

    if (gridMouse) {
      debugTime('tooltips:get');
      if (isOverGraph) {
        const hovered = this.hoverCursor.indexes;
        this.setInterfaceProperty('isPinnable', this.isPinnable = !!hovered.length);
        if (isMouseClicked) {
          if (hovered.length) {
            // Lock new position if tooltips to display at click point
            this.pinGraphMouse();
          } else {
            // Clear lock if no tooltips at click point
            this.unpinGraphMouse();
          }
        }
      }
      debugTimeEnd('tooltips:get');
    }

    this.pinCursor.setRadius(tooltipDistance).updateCursor();

    debugTime('axes', this.drawAxes);
    this.updateLinesRect();
    this.drawLinesAndNodes();
    this.drawCrosshair();

    debugTime('final draws');
    resizeStyleRect(coverElement.style, width, height);
    if (cursorType === 'vertical') {
      resizeStyleRect(verticalCursorEl.style, 1, graphHeight);
      resizeStyleRect(verticalSecondaryCursorEl.style, 1, graphHeight);
    }
    clearCanvas(mainCanvas.getCanvas(), '#fff', width, height, ctxOptions);
    debugTimeEnd('final draws');

    this.drawExtras();

    debugTimeEnd('draw');
    debugGroupEnd();
  }

  @action drawAxes() {
    const {
      config, bottomAxisCanvas, leftAxisCanvas,
      timeSeriesOutputs: { colorGraph },
    } = this;
    const { xAxisSize, yAxisSize } = config;
    this.drawAxis(CHANGE_X_AXIS, bottomAxisCanvas,
        config.xAxisLeft, config.xAxisTop, xAxisSize.width, xAxisSize.height,
        colorGraph?.xAxisBgColor, config.xScale, config.xAxisOptions
    );
    this.drawAxis(CHANGE_Y_AXIS, leftAxisCanvas,
        config.yAxisLeft, config.yAxisTop, yAxisSize.width, yAxisSize.height,
        colorGraph?.yAxisBgColor, config.yScale, config.yAxisOptions
    );
  }

  drawAxis(CHANGES_KEY, canvas, x, y, width, height, bgColor, scale, options) {
    if (!(width && height && this.changes.consume(CHANGES_KEY))) {
      return;
    }
    Object.assign(canvas.style, {
      transform: `translate(${x}px, ${y}px)`,
    });
    clearCanvas(canvas, bgColor || '#fff', width, height, ctxOptions);
    drawGraphAxis(ctx(canvas, true), scale, options);
  }

  updateLinesRect() {
    const { config, linesEl, lineBackgroundElement, tooltipsEl } = this;
    const { graphTop, graphLeft, graphWidth, graphHeight } = config;
    resizeStyleRect(lineBackgroundElement.style, graphWidth, graphHeight);
    Object.assign(lineBackgroundElement.style, {
      transform: `translate(${graphLeft}px, ${graphTop}px)`,
    });
    Object.assign(linesEl.style, {
      transform: `translate(${graphLeft}px, ${graphTop}px)`,
    });
    Object.assign(tooltipsEl.style, {
      transform: `translate(${graphLeft}px, ${graphTop}px)`,
    });
  }

  /**
   * Called each time vertex is drawn to get scaled position
   * @param {GraphViewVertex} vertex
   * @returns {*}
   */
  @action vertexTransform(vertex) {
    // Only transform once per render
    if (vertex.transformRenderIndex !== this.renderIndex) {
      vertex.transformRenderIndex = this.renderIndex;
      vertex.x = this.config.graphMargin + this.config.xScale(vertex.xValue);
      vertex.y = this.config.graphMargin + this.config.yScale(vertex.yValue);
    }
    return vertex;
  }

  lineDrawOptions = {
    transform: this.vertexTransform,
  };

  nodeDrawOptions = {
    ...this.lineDrawOptions,
    size: this.config.singleNodeSize,
  };

  @action drawLinesAndNodes() {
    if (!(this.isRenderingAll || this.hasMouseMovedThisRender || this.hasLineMetaChangedThisRender)) {
      return;
    }
    const {
      /** @type ModelRunMemberLine[] */ itemGroups: modelRunMembers
    } = this.modelRunMemberVertexGroup;
    this.drawTooltipsStart();
    for (const group of modelRunMembers) {
      if (group.isVisible) {
        this.drawGroupLinesAndNodes(group);
        this.drawGroupTooltips(group);
      }
    }
    this.drawTooltipsEnd();
    debugTimeEnd('lines/nodes');
    debugTimeEnd('tooltips');
  }

  drawGroupLinesAndNodes(group) {
    if (!this.isRenderingAll) {
      return;
    }
    debugTime('lines/nodes');
    this.clearLineCanvas(group.lineCanvas);
    const lineContext = this.getLineContext(group.lineCanvas);
    const color = group.dataKeyColor();
    lineContext.strokeStyle = color;
    lineContext.beginPath();
    drawLines(lineContext, group.linesAndNodes.lines, this.lineDrawOptions);
    lineContext.stroke();
    this.setNodeContextColors(lineContext, color);
    drawGraphNodes(lineContext, group.linesAndNodes.nodes, this.nodeDrawOptions);
    debugTimePause('lines/nodes');
  }

  @action drawTooltipsStart() {
    const { hoverCursor: { indexes: hovered }, pinCursor: { indexes: pinned } } = this;
    const { lineMeta } = this.args;
    const {
      /** @type ModelRunMemberLine[] */ itemGroups: modelRunMembers
    } = this.modelRunMemberVertexGroup;
    this.isRenderingTooltips = this.hasTooltipsReset
      || (this.isRenderingAll || this.hasMouseMovedThisRender)
      && (pinned.length || hovered.length);
    for (const group of modelRunMembers) {
      group.isVisible = modelRunMembers.length <= 1 || lineMeta?.[group.dataKeyIndex]?.visible;
      const visibility = group.isVisible ? '' : 'hidden';
      // Hide canvases for model-run-members user has hidden
      const hasLineBecomeVisible = softSet(group.lineCanvas.style, 'visibility', visibility);
      const hasTooltipBecomeVisible = softSet(group.tooltipCanvas.style, 'visibility', visibility);
      group.hasBecomeVisible = group.isVisible && (hasLineBecomeVisible || hasTooltipBecomeVisible);
      this.isRenderingTooltips ||= group.hasBecomeVisible;
    }
    if (!this.isRenderingTooltips) {
      return;
    }
    debugTime('tooltips');
    this.resetTooltips();
    debugTimePause('tooltips');
  }

  resetTooltips() {
    if (this.tooltips?.length) {
      this.tooltips = [];
    }
    if (this.secondaryTooltips?.length) {
      this.secondaryTooltips = [];
    }
    this.hasTooltipsReset = true;
  }

  drawGroupTooltips(group) {
    if (!this.isRenderingTooltips) {
      return;
    }
    debugTime('tooltips');
    const { hoverCursor: { indexes: hovered }, pinCursor: { indexes: pinned } } = this;
    this.clearTooltipCanvas(group.tooltipCanvas);
    const tooltipContext = this.getTooltipContext(group.tooltipCanvas);
    const color = group.dataKeyColor();
    this.setNodeContextColors(tooltipContext, color);
    this.drawGroupTooltipType(group, tooltipContext, hovered, this.tooltips, this.hoverCursor);
    this.drawGroupTooltipType(group, tooltipContext, pinned, this.secondaryTooltips, this.pinCursor);
    debugTimePause('tooltips');
  }

  drawGroupTooltipType(group, context, dataIndexesUnderCursor, tooltips, cursor) {
    const { _data: data } = this;
    const { dataKeyLabels: labelFn } = this.args;
    const { nodeSize } = this.config;
    const { dataKey, dataKeyIndex } = group;
    const name = labelFn?.(dataKey, dataKeyIndex) || dataKey;
    const rightmostPixel = this.pixelIndex.getPixelByDataIndex(data.length - 1);
    const isRightmostPixel = dataIndexesUnderCursor.length && rightmostPixel === cursor.closest;
    // Requirement to choose least/latest date at position (part 1/2)
    let bound;
    for (const i of dataIndexesUnderCursor) {
      const { xValue } = data[i];
      if (bound === undefined || (isRightmostPixel ? xValue > bound : xValue < bound)) {
        bound = xValue;
      }
    }
    let defaultNode;
    for (const i of dataIndexesUnderCursor) {
      if (data[i].group === group) {
        const { xValue } = data[i];
        // Requirement to choose least/latest date at position (part 2/2)
        const isWithinBound = Math.abs(xValue - bound) < 1;
        if (!isWithinBound) {
          continue;
        }
        if (!defaultNode) {
          defaultNode = data[i];
        }
        const isValid = this.isVertexSet(data[i]);
        if (isValid) {
          // Draw tooltip on graph
          group.drawNode(context, data[i].x, data[i].y, nodeSize);
          // Store valid tooltips to be exposed and drawn later to legend canvases
          this.createTooltip(tooltips, name, data[i], isValid);
          defaultNode = null;
          break;
        }
      }
      // Use first in-bounds null if none other found
      if (defaultNode) {
        this.createTooltip(tooltips, name, defaultNode, false);
      }
    }
  }

  /**
   * @name GraphViewTooltip
   * @property {string|undefined} name Name of model-run-member
   * @property {boolean} noValue Whether tooltip is of null value
   * @property {number} dataKeyIndex
   * @property {*} xValue
   * @property {*} yValue
   * @property {GraphViewTooltip[]} collection (e.g. this.tooltips)
   */

  /**
   * @type {GraphViewTooltip[]}
   */
  tooltipPool = [];

  createTooltip(tooltips, name, vertex, isValidValue) {
    const { tooltipPool } = this;
    const { xValue, yValue, group: { dataKeyIndex } } = vertex;
    let tooltip;
    // Look for a matching tooltip in pool to avoid creating/destroying object/canvas
    for (const t of tooltipPool) {
      if (t.dataKeyIndex === dataKeyIndex && t.collection === tooltips) {
        tooltip = t;
        break;
      }
    }
    if (!tooltip) {
      // Create new tooltip and add to pool
      tooltip = tooltipPool[tooltipPool.length] = {
        dataKeyIndex,
        collection: tooltips,
        _canvas: this.createTooltipLegendCanvas(),
      };
    }
    // Assign current tooltip values
    Object.assign(tooltip, {
      name,
      noValue: !isValidValue,
      value: isValidValue ? yValue : undefined,
      date: xValue,
      canvas: isValidValue && this.tooltipCanvasSize && tooltip._canvas || null,
      vertex,
    });
    // Add tooltip to collection
    tooltips[tooltips.length] = tooltip;
  }

  drawTooltipsEnd() {
    if (!this.isRenderingTooltips) {
      return;
    }
    debugTime('tooltips');
    const { tooltipCanvasSize } = this;
    const { hoverCursor: { indexes: hovered }, pinCursor: { indexes: pinned } } = this;
    // Clear and resize the buffer canvas to handle all legend tooltips
    const width = (tooltipCanvasSize && tooltipCanvasSize.width || 0)
      * (hovered.length + pinned.length);
    const height = tooltipCanvasSize && tooltipCanvasSize.height || 0;
    if (debug.useTooltipBuffer) {
      clearCanvas(this.tooltipBufferCanvas, features.alpha ? null : '#fff', width, height, ctxOptions);
      const tooltipBufferContext = ctx(this.tooltipBufferCanvas);
      const {
        /** @type ModelRunMemberLine[] */ itemGroups: modelRunMembers
      } = this.modelRunMemberVertexGroup;
      for (const group of modelRunMembers) {
        if (debug.useTooltipBuffer) {
          this.drawGroupToLegendTooltipBuffer(group, tooltipBufferContext);
        }
      }
      this.drawLegendTooltipBufferToLegendTooltips();
    } else {
      this.drawLegendTooltipsDirect();
    }
    // Move tooltip vertical cursor lines
    const graphLeft = this.config.graphLeft + this.config.graphMargin;
    const hoverChanged = softSet(this, '_hoverClosest', this.hoverCursor.closest);
    if (hoverChanged) {
      if (isSet(this.hoverCursor.closest)) {
        softSetProperties(this.verticalCursorEl.style, {
          display: '',
          transform: `translate(${graphLeft + this.hoverCursor.closest}px, ${this.config.graphTop}px)`,
        });
      } else {
        this.verticalCursorEl.style.display = 'none';
      }
    }
    const pinChanged = softSet(this, '_pinClosest', this.pinCursor.closest);
    if (pinChanged) {
      const isPinned = this._isPinned = isSet(this.pinCursor.closest);
      this.setInterfaceProperty('isPinned', isPinned);
      if (isPinned) {
        softSetProperties(this.verticalSecondaryCursorEl.style, {
          display: '',
          transform: `translate(${graphLeft + this.pinCursor.closest}px, ${this.config.graphTop}px)`,
        });
      } else {
        this.verticalSecondaryCursorEl.style.display = 'none';
      }
    }
    // Emit event for consumer
    if (features.tooltipEvent) {
      call(this.args.onTooltipChange, null, this.tooltips, this.secondaryTooltips);
    }
    this.hasTooltipsReset = false;
    debugTimePause('tooltips');
  }

  drawGroupToLegendTooltipBuffer(group, context) {
    const { tooltipCanvasSize } = this;
    const { lineCanvas } = group;
    const width = tooltipCanvasSize && tooltipCanvasSize.width || 0;
    const height = tooltipCanvasSize && tooltipCanvasSize.height || 0;
    const ratio = window.devicePixelRatio;
    const rw = width * ratio;
    const rh = height * ratio;
    const cx = width / 2;
    const cy = height / 2;
    const tooltipTypes = [ this.tooltips, this.secondaryTooltips ];
    let i = 0;
    for (let t = 0; t < tooltipTypes.length; ++t) {
      for (const tooltip of tooltipTypes[t]) {
        const { canvas, vertex } = tooltip;
        if (!canvas) {
          continue;
        }
        const x = width * i;
        context.drawImage(lineCanvas, (vertex.x - cx) * ratio, (vertex.y - cy) * ratio, rw, rh, x, 0, width, height);
        ++i;
      }
    }
  }

  drawLegendTooltipsDirect() {
    const {
      /** @type ModelRunMemberLine[] */ itemGroups: modelRunMembers
    } = this.modelRunMemberVertexGroup;
    const { tooltipCanvasSize, config: { nodeSize } } = this;
    const width = tooltipCanvasSize && tooltipCanvasSize.width || 0;
    const height = tooltipCanvasSize && tooltipCanvasSize.height || 0;
    const ratio = window.devicePixelRatio;
    const rw = width * ratio;
    const rh = height * ratio;
    const cx = width / 2;
    const cy = height / 2;
    const tooltipTypes = [ this.tooltips, this.secondaryTooltips ];
    for (let t = 0; t < tooltipTypes.length; ++t) {
      for (const tooltip of tooltipTypes[t]) {
        const { canvas, vertex } = tooltip;
        if (!canvas) {
          continue;
        }
        const context = ctx(canvas);
        // Draw each line group to the legend tooltip
        for (const group of modelRunMembers) {
          context.drawImage(group.lineCanvas, (vertex.x - cx) * ratio, (vertex.y - cy) * ratio, rw, rh, 0, 0, width, height);
        }
        // Draw the selected node in the middle of the legend tooltip
        this.setNodeContextColors(context, vertex.group.dataKeyColor());
        vertex.group.drawNode(context, cx, cy, nodeSize);
      }
    }
  }

  drawLegendTooltipBufferToLegendTooltips() {
    const { tooltipCanvasSize, tooltipBufferCanvas, config: { nodeSize } } = this;
    const ratio = window.devicePixelRatio;
    const width = tooltipCanvasSize && tooltipCanvasSize.width || 0;
    const height = tooltipCanvasSize && tooltipCanvasSize.height || 0;
    const rw = width * ratio;
    const rh = height * ratio;
    const cx = width / 2;
    const cy = height / 2;
    const tooltipTypes = [ this.tooltips, this.secondaryTooltips ];
    let i = 0;
    const tooltipBufferContext = ctx(tooltipBufferCanvas);
    for (let t = 0; t < tooltipTypes.length; ++t) {
      for (const tooltip of tooltipTypes[t]) {
        const { vertex, canvas } = tooltip;
        if (!canvas) {
          continue;
        }
        const x = width * i;
        const context = this.getTooltipLegendContext(canvas);
        if (debug.debugTooltipBuffer) {
          // Helps to verify below drawImage is correct
          tooltipBufferContext.fillStyle = vertex.group.dataKeyColor();
          drawRectangles(tooltipBufferContext, x, 0, width, height, 3, 3, true, false, true);
        }
        // Draw the buffered lines to the legend tooltip
        context.drawImage(tooltipBufferCanvas, x * ratio, 0, rw, rh, 0, 0, width, height);
        // Draw the selected node in the middle of the legend tooltip
        this.setNodeContextColors(context, vertex.group.dataKeyColor());
        vertex.group.drawNode(context, cx, cy, nodeSize);
        ++i;
      }
    }
  }

  setNodeContextColors(context, color) {
    context.strokeStyle = 'rgba(0, 0, 0, 1)';
    context.fillStyle = alpha(color, .5);
  }

  drawCrosshair() {
    const { graphMouseX: x, graphMouseY: y } = this;
    if (x === null || !(debug.crosshair && (this.isRenderingAll || this.hasMouseMovedThisRender))) {
      return;
    }
    const padding = this.config.graphMargin;
    drawGraphCrosshair(
      ctx(this.tooltipCanvas), x + padding, y + padding,
     this.config.tooltipDistance * 2, '#000'
    );
  }

  drawExtras() {
    if (DEBUG) {
      const { config } = this;
      const { legendOptions } = config;
      if (config.headerHeight) {
        const header = DprCanvas.create(config.headerWidth, config.headerHeight);
        drawGraphText(header.getContext(), config.headerOptions);
        this.mainCanvas.drawImage(header, config.headerLeft || 0, config.headerTop || 0);
      }
      if (config.footerHeight) {
        const { footerOptions, footerLeft, footerTop, footerWidth } = config;
        const footer = DprCanvas.create(footerWidth, config.footerHeight);
        drawGraphText(footer.getContext(), footerOptions);
        this.mainCanvas.drawImage(footer, footerLeft || 0, footerTop || 0);
      }
      if ((config.legendRight || config.legendBottom) && legendOptions.width && legendOptions.height) {
        const legend = DprCanvas.create(
          legendOptions.width, legendOptions.height,
            this.timeSeriesOutputs.colorGraph?.legendBgColor
        );
        drawGraphLegend(legend.getContext(), config.legendKeys, legendOptions);
        this.mainCanvas.drawImage(legend,
            config[config.legendRight ? 'legendRightLeft' : 'legendBottomLeft'] || 0,
            config[config.legendRight ? 'legendRightTop' : 'legendBottomTop'] || 0
        );
      }
    }
  }

  <template>
    {{#let
      (classModConfig "c-graph-view")
      (passProperties this.config.input)
      as | classModCfg config |
    ~}}
    {{#let
      (classModConfig (classMod config=classModCfg suffix="__line"))
      as | lineClassConfig |
    ~}}
    <div ...attributes
      class={{classMod ""
        (not @data.length) "--empty"
        config=classModCfg
      }}
      {{config "data" @data}}
      {{config "width" @width}}
      {{config "height" @height}}
      {{config "xLabel" @xLabel}}
      {{config "xKey" @xKey}}
      {{config "xTickAngle" @xTickAngle}}
      {{config "yKey" @yKey}}
      {{config "yLabel" @yLabel}}
      {{config "lineColor" @lineColor}}
      {{config "tickArguments" @tickArguments}}
      {{config "tickFormat" @tickFormat}}
      {{config "tickMaxFormat" @tickMaxFormat}}
      {{config "fontSize" @fontSize}}
      {{config "fontColor" @fontColor}}
      {{config "nodeSize" @nodeSize}}
      {{config "cursorType" @cursorType}}
      {{config "tooltipDistance" @tooltipDistance}}
      {{config "paddingLeft" @marginLeft}}
      {{config "paddingRight" @marginRight}}
      {{config "legendRight" @legendRight}}
      {{config "legendBottom" @legendBottom}}
      {{config "headings" @headings}}
      {{config "footers" @footers}}
      {{config "dataKeyColors" @dataKeyColors}}
      {{config "dataKeyColorsCyclic" @dataKeyColorsCyclic}}
      {{config "dataKeyLabels" @dataKeyLabels}}
      {{config "dataKeyIndex" @dataKeyIndex}}
      {{didInsert this.didInsertWrapper (hash
        hoverClass=(classMod config=classModCfg suffix="--hover")
        linesClass=(classMod config=classModCfg suffix="__lines")
        lineClass=(classMod config=lineClassConfig)
        cursorClass=(classMod config=classModCfg suffix="__cursor")
        verticalCursorClass=(concatClasses "is-non-touch-only" (classMod config=classModCfg suffix="__vertical-cursor"))
        verticalSecondaryCursorClass=(classMod config=classModCfg suffix="__vertical-secondary-cursor")
        inactiveLineClass=(classMod config=lineClassConfig suffix="--inactive")
        activeLineClass=(classMod config=lineClassConfig suffix="--active")
        axisClass=(classMod config=classModCfg suffix="__axis")
        leftAxisClass=(classMod config=classModCfg suffix="__axis--left")
        bottomAxisClass=(classMod config=classModCfg suffix="__axis--bottom")
        tooltipsClass=(classMod config=classModCfg suffix="__tooltips")
        tooltipClass=(classMod config=classModCfg suffix="__tooltip")
        coverClass=(classMod config=classModCfg suffix="__cover")
        tooltipCanvasClass=(classMod config=classModCfg suffix="__tooltip-canvas")
      )}}
      {{update this.onInterfaceObjectChange @interface}}
      {{update this.onSizeChange this.config.width this.config.height}}
      {{update this.onDataChange (emberArray @data)}}
      {{update this.onDomainChange @xLowerDomain @xUpperDomain @yLowerDomain @yUpperDomain}}
      {{update this.onWindowChange @xLowerWindow @xUpperWindow @yLowerWindow @yUpperWindow}}
      {{update this.onScaleChange this.config.xScale this.config.yScale}}
      {{update this.onDataKeyColorChange @dataKeyColor}}
      {{update this.onTooltipSizeChange @tooltipCanvasWidth @tooltipCanvasHeight}}
      {{update this.onLineMetaChange (emberArray @lineMeta)}}
      {{update this.onCursorChange @cursorType}}
      {{update @onGraphRectChange this.config.graphRect}}
    />
    {{yield}}
    {{/let}}
    {{/let}}
  </template>
}
